import { all, call, put, takeEvery } from 'redux-saga/effects';
import apiCall from '../services';

const apiUrl = process.env.REACT_APP_API_URL;
const enviromentUrl = process.env.REACT_APP_ENVIROMENT;

const getSetCookie = () => window.localStorage.getItem('uuidSetCookie');

const appendEnvParam = (url) => {
	// follow same logic as other sagas: check for non-empty enviromentUrl
	if (enviromentUrl !== '') {
		return url.includes('?')
			? `${url}&env=${enviromentUrl}`
			: `${url}?env=${enviromentUrl}`;
	}
	return url;
};

const createHeaders = (access) => ({
	'Content-Type': 'application/json',
	Authorization: `Bearer ${access}`,
	'X-RealmUuid': getSetCookie(),
});

const createRequestOptions = (access, payload) => {
	const headers = createHeaders(access);

	if (payload !== undefined) {
		return {
			headers,
			body: JSON.stringify(payload),
		};
	}

	return { headers };
};

const sanitizePayload = (payload) => {
	if (!payload || typeof payload !== 'object') {
		return payload;
	}

	const cloned = { ...payload };

	if (Object.prototype.hasOwnProperty.call(cloned, 'uuid')) {
		delete cloned.uuid;
	}

	return cloned;
};

const buildMutationResponse = (response) => {
	if (!response) {
		return { responseStatus: 'error' };
	}

	let responseStatus = 'warning';

	if (typeof response.status === 'number') {
		responseStatus =
			response.status >= 200 && response.status < 300 ? 'success' : 'error';
	} else if (response.uuid || response.createdAt || response.updatedAt) {
		responseStatus = 'success';
	} else if (response.success === true) {
		responseStatus = 'success';
	}

	if (typeof response.status === 'number') {
		return {
			status: response.status,
			responseStatus,
		};
	}

	if (typeof response === 'object') {
		return {
			...response,
			responseStatus,
		};
	}

	return {
		value: response,
		responseStatus,
	};
};

const buildListUrl = (path, query, page, includes) => {
	let url = `${apiUrl}${path}`;

	// Add env first (if present) so it becomes the first query param
	if (enviromentUrl !== '') {
		url += `?env=${enviromentUrl}`;
	}

	// append includes param if provided (normalize to string) and place it after env
	if (includes !== undefined && includes !== null && includes !== '') {
		const includeValue = Array.isArray(includes)
			? includes.join(',')
			: String(includes);
		const joinChar = url.includes('?') ? '&' : '?';
		// includeValue intentionally not encoded so commas remain as-is
		url += `${joinChar}include=${includeValue}`;
	}

	// append query (which usually starts with '?page_size=...&page=...') or page param last
	if (query) {
		// query may start with '?', strip it and append with proper separator
		const q = query.startsWith('?') ? query.substring(1) : query;
		const joinChar = url.includes('?') ? '&' : '?';
		url += `${joinChar}${q}`;
	} else if (page) {
		const joinChar = url.includes('?') ? '&' : '?';
		url += `${joinChar}page=${page}`;
	}

	return url;
};

const buildDetailUrl = (path) => appendEnvParam(`${apiUrl}${path}`);

function* postStatement(actions) {
	try {
		const requestOptions = createRequestOptions(
			actions.access,
			actions.payload
		);
		const url = appendEnvParam(`${apiUrl}/opa-statement`);
		const response = yield call(apiCall, 'POST', url, requestOptions);
		yield put({
			type: 'STATEMENT_RESPONSE_POST',
			value: buildMutationResponse(response),
		});
	} catch (error) {
		yield put({
			type: 'STATEMENT_RESPONSE_POST',
			value: { ...error, responseStatus: 'error' },
		});
	}
}

function* getStatements(actions) {
	try {
		const requestOptions = createRequestOptions(actions.access);
		const url = buildListUrl('/opa-statements', actions.query, actions.page);
		const response = yield call(apiCall, 'get', url, requestOptions);
		yield put({ type: 'STATEMENTS_RESPONSE_GET', value: response });
	} catch (error) {
		yield put({ type: 'STATEMENTS_RESPONSE_GET', value: error });
	}
}

function* getStatement(actions) {
	try {
		const requestOptions = createRequestOptions(actions.access);
		const url = buildDetailUrl(`/opa-statement/${actions.uuid}`);
		const response = yield call(apiCall, 'get', url, requestOptions);
		yield put({ type: 'STATEMENT_RESPONSE_GET', value: response });
	} catch (error) {
		yield put({ type: 'STATEMENT_RESPONSE_GET', value: error });
	}
}

function* patchStatement(actions) {
	try {
		const payload = sanitizePayload(actions.payload);
		const requestOptions = createRequestOptions(actions.access, payload);
		const url = buildDetailUrl(`/opa-statement/${actions.uuid}`);
		const response = yield call(apiCall, 'PATCH', url, requestOptions);
		yield put({
			type: 'STATEMENT_RESPONSE_PATCH',
			value: buildMutationResponse(response),
		});
	} catch (error) {
		yield put({
			type: 'STATEMENT_RESPONSE_PATCH',
			value: { ...error, responseStatus: 'error' },
		});
	}
}

function* deleteStatement(actions) {
	try {
		const requestOptions = createRequestOptions(actions.access);
		const url = buildDetailUrl(`/opa-statement/${actions.uuid}`);
		const response = yield call(apiCall, 'DELETE', url, requestOptions);
		yield put({
			type: 'STATEMENT_RESPONSE_DELETE',
			value: buildMutationResponse(response),
		});
	} catch (error) {
		yield put({
			type: 'STATEMENT_RESPONSE_DELETE',
			value: { ...error, responseStatus: 'error' },
		});
	}
}

function* postPropertyMap(actions) {
	try {
		const requestOptions = createRequestOptions(
			actions.access,
			actions.payload
		);
		const url = appendEnvParam(`${apiUrl}/opa-property-map`);
		const response = yield call(apiCall, 'POST', url, requestOptions);
		yield put({
			type: 'PROPERTY_MAP_RESPONSE_POST',
			value: buildMutationResponse(response),
		});
	} catch (error) {
		yield put({
			type: 'PROPERTY_MAP_RESPONSE_POST',
			value: { ...error, responseStatus: 'error' },
		});
	}
}

function* getPropertyMaps(actions) {
	try {
		const requestOptions = createRequestOptions(actions.access);
		const url = buildListUrl(
			'/opa-property-maps',
			actions.query,
			actions.page,
			actions.includes
		);
		const response = yield call(apiCall, 'get', url, requestOptions);
		yield put({ type: 'PROPERTY_MAPS_RESPONSE_GET', value: response });
	} catch (error) {
		yield put({ type: 'PROPERTY_MAPS_RESPONSE_GET', value: error });
	}
}

function* getPropertyMap(actions) {
	try {
		const requestOptions = createRequestOptions(actions.access);
		let url = buildDetailUrl(`/opa-property-map/${actions.uuid}`);
		// allow optional includes similar to list endpoint
		if (actions.includes) {
			const includeValue = Array.isArray(actions.includes)
				? actions.includes.join(',')
				: String(actions.includes);
			const joinChar = url.includes('?') ? '&' : '?';
			url += `${joinChar}include=${includeValue}`;
		}
		const response = yield call(apiCall, 'get', url, requestOptions);
		yield put({ type: 'PROPERTY_MAP_RESPONSE_GET', value: response });
	} catch (error) {
		yield put({ type: 'PROPERTY_MAP_RESPONSE_GET', value: error });
	}
}

function* patchPropertyMap(actions) {
	try {
		const payload = sanitizePayload(actions.payload);
		const requestOptions = createRequestOptions(actions.access, payload);
		const url = buildDetailUrl(`/opa-property-map/${actions.uuid}`);
		const response = yield call(apiCall, 'PATCH', url, requestOptions);
		yield put({
			type: 'PROPERTY_MAP_RESPONSE_PATCH',
			value: buildMutationResponse(response),
		});
	} catch (error) {
		yield put({
			type: 'PROPERTY_MAP_RESPONSE_PATCH',
			value: { ...error, responseStatus: 'error' },
		});
	}
}

function* deletePropertyMap(actions) {
	try {
		const requestOptions = createRequestOptions(actions.access);
		const url = buildDetailUrl(`/opa-property-map/${actions.uuid}`);
		const response = yield call(apiCall, 'DELETE', url, requestOptions);
		yield put({
			type: 'PROPERTY_MAP_RESPONSE_DELETE',
			value: buildMutationResponse(response),
		});
	} catch (error) {
		yield put({
			type: 'PROPERTY_MAP_RESPONSE_DELETE',
			value: { ...error, responseStatus: 'error' },
		});
	}
}

function* deletePropertyMapImage(actions) {
	try {
		const requestOptions = createRequestOptions(actions.access);
		const url = buildDetailUrl(
			`/opa-property-map-image/${actions.propertyMapUuid}/${actions.imageUuid}`
		);
		const response = yield call(apiCall, 'DELETE', url, requestOptions);
		yield put({
			type: 'PROPERTY_MAP_IMAGE_RESPONSE_DELETE',
			value: buildMutationResponse(response),
		});
	} catch (error) {
		yield put({
			type: 'PROPERTY_MAP_IMAGE_RESPONSE_DELETE',
			value: { ...error, responseStatus: 'error' },
		});
	}
}

function* deletePropertyMapArea(actions) {
	try {
		const requestOptions = createRequestOptions(actions.access);
		const normalize = (v) => String(v || '').split('/').pop();
		const propertyMapUuid = normalize(actions.propertyMapUuid);
		const areaUuid = normalize(actions.areaUuid);
		const url = buildDetailUrl(
			`/opa-property-map-area/${propertyMapUuid}/${areaUuid}`
		);
		const response = yield call(apiCall, 'DELETE', url, requestOptions);
		yield put({
			type: 'PROPERTY_MAP_AREA_RESPONSE_DELETE',
			value: buildMutationResponse(response),
		});
	} catch (error) {
		yield put({
			type: 'PROPERTY_MAP_AREA_RESPONSE_DELETE',
			value: { ...error, responseStatus: 'error' },
		});
	}
}

function* deletePropertyMapPoi(actions) {
	try {
		const requestOptions = createRequestOptions(actions.access);
		const normalize = (v) => String(v || '').split('/').pop();
		const propertyMapUuid = normalize(actions.propertyMapUuid);
		const poiUuid = normalize(actions.poiUuid);
		const url = buildDetailUrl(
			`/opa-property-map-poi/${propertyMapUuid}/${poiUuid}`
		);
		const response = yield call(apiCall, 'DELETE', url, requestOptions);
		yield put({
			type: 'PROPERTY_MAP_POI_RESPONSE_DELETE',
			value: buildMutationResponse(response),
		});
	} catch (error) {
		yield put({
			type: 'PROPERTY_MAP_POI_RESPONSE_DELETE',
			value: { ...error, responseStatus: 'error' },
		});
	}
}

function* postArea(actions) {
	try {
		const requestOptions = createRequestOptions(
			actions.access,
			actions.payload
		);
		const url = appendEnvParam(`${apiUrl}/opa-area`);
		const response = yield call(apiCall, 'POST', url, requestOptions);
		yield put({
			type: 'AREA_RESPONSE_POST',
			value: buildMutationResponse(response),
		});
	} catch (error) {
		yield put({
			type: 'AREA_RESPONSE_POST',
			value: { ...error, responseStatus: 'error' },
		});
	}
}

function* getAreas(actions) {
	try {
		const requestOptions = createRequestOptions(actions.access);
		const url = buildListUrl(
			'/opa-areas',
			actions.query,
			actions.page,
			actions.includes
		);
		const response = yield call(apiCall, 'get', url, requestOptions);
		yield put({ type: 'AREAS_RESPONSE_GET', value: response });
	} catch (error) {
		yield put({ type: 'AREAS_RESPONSE_GET', value: error });
	}
}

function* getArea(actions) {
	try {
		const requestOptions = createRequestOptions(actions.access);
		let url = buildDetailUrl(`/opa-area/${actions.uuid}`);
		// Permitir includes como en getAreas
		if (actions.includes) {
			const includeValue = Array.isArray(actions.includes)
				? actions.includes.join(',')
				: String(actions.includes);
			const joinChar = url.includes('?') ? '&' : '?';
			url += `${joinChar}include=${includeValue}`;
		}
		const response = yield call(apiCall, 'get', url, requestOptions);
		yield put({ type: 'AREA_RESPONSE_GET', value: response });
	} catch (error) {
		yield put({ type: 'AREA_RESPONSE_GET', value: error });
	}
}

function* patchArea(actions) {
	try {
		const payload = sanitizePayload(actions.payload);
		const requestOptions = createRequestOptions(actions.access, payload);
		const url = buildDetailUrl(`/opa-area/${actions.uuid}`);
		const response = yield call(apiCall, 'PATCH', url, requestOptions);
		yield put({
			type: 'AREA_RESPONSE_PATCH',
			value: buildMutationResponse(response),
		});
	} catch (error) {
		yield put({
			type: 'AREA_RESPONSE_PATCH',
			value: { ...error, responseStatus: 'error' },
		});
	}
}

function* deleteArea(actions) {
	try {
		const requestOptions = createRequestOptions(actions.access);
		const url = buildDetailUrl(`/opa-area/${actions.uuid}`);
		const response = yield call(apiCall, 'DELETE', url, requestOptions);
		yield put({
			type: 'AREA_RESPONSE_DELETE',
			value: buildMutationResponse(response),
		});
	} catch (error) {
		yield put({
			type: 'AREA_RESPONSE_DELETE',
			value: { ...error, responseStatus: 'error' },
		});
	}
}

function* deleteAreaPool(actions) {
	try {
		const requestOptions = createRequestOptions(actions.access);
		const normalize = (v) => String(v || '').split('/').pop();
		const areaUuid = normalize(actions.areaUuid);
		const poolUuid = normalize(actions.poolUuid);
		const url = buildDetailUrl(
			`/opa-area-pool/${areaUuid}/${poolUuid}`
		);
		const response = yield call(apiCall, 'DELETE', url, requestOptions);
		yield put({
			type: 'AREA_POOL_RESPONSE_DELETE',
			value: buildMutationResponse(response),
		});
	} catch (error) {
		yield put({
			type: 'AREA_POOL_RESPONSE_DELETE',
			value: { ...error, responseStatus: 'error' },
		});
	}
}

function* deleteAreaPoi(actions) {
	try {
		const requestOptions = createRequestOptions(actions.access);
		const normalize = (v) => String(v || '').split('/').pop();
		const areaUuid = normalize(actions.areaUuid);
		const poiUuid = normalize(actions.poiUuid);
		const url = buildDetailUrl(
			`/opa-area-poi/${areaUuid}/${poiUuid}`
		);
		const response = yield call(apiCall, 'DELETE', url, requestOptions);
		yield put({
			type: 'AREA_POI_RESPONSE_DELETE',
			value: buildMutationResponse(response),
		});
	} catch (error) {
		yield put({
			type: 'AREA_POI_RESPONSE_DELETE',
			value: { ...error, responseStatus: 'error' },
		});
	}
}

function* deleteAreaImage(actions) {
	try {
		const requestOptions = createRequestOptions(actions.access);
		const url = buildDetailUrl(
			`/opa-area-image/${actions.areaUuid}/${actions.imageUuid}`
		);
		const response = yield call(apiCall, 'DELETE', url, requestOptions);
		yield put({
			type: 'AREA_IMAGE_RESPONSE_DELETE',
			value: buildMutationResponse(response),
		});
	} catch (error) {
		yield put({
			type: 'AREA_IMAGE_RESPONSE_DELETE',
			value: { ...error, responseStatus: 'error' },
		});
	}
}

function* resetAreas() {
	yield put({ type: "AREAS_RESET" });
}

function* postPool(actions) {
	try {
		const requestOptions = createRequestOptions(
			actions.access,
			actions.payload
		);
		const url = appendEnvParam(`${apiUrl}/opa-pool`);
		const response = yield call(apiCall, 'POST', url, requestOptions);
		yield put({
			type: 'POOL_RESPONSE_POST',
			value: buildMutationResponse(response),
		});
	} catch (error) {
		yield put({
			type: 'POOL_RESPONSE_POST',
			value: { ...error, responseStatus: 'error' },
		});
	}
}

function* getPools(actions) {
	try {
		const requestOptions = createRequestOptions(actions.access);
		const url = buildListUrl(
			'/opa-pools',
			actions.query,
			actions.page,
			actions.includes
		);
		const response = yield call(apiCall, 'get', url, requestOptions);
		yield put({ type: 'POOLS_RESPONSE_GET', value: response });
	} catch (error) {
		yield put({ type: 'POOLS_RESPONSE_GET', value: error });
	}
}

function* getPool(actions) {
	try {
		const requestOptions = createRequestOptions(actions.access);
		let url = buildDetailUrl(`/opa-pool/${actions.uuid}`);
		// Permitir includes como en getArea
		if (actions.includes) {
			const includeValue = Array.isArray(actions.includes)
				? actions.includes.join(',')
				: String(actions.includes);
			const joinChar = url.includes('?') ? '&' : '?';
			url += `${joinChar}include=${includeValue}`;
		}
		const response = yield call(apiCall, 'get', url, requestOptions);
		yield put({ type: 'POOL_RESPONSE_GET', value: response });
	} catch (error) {
		yield put({ type: 'POOL_RESPONSE_GET', value: error });
	}
}

// Fetch pool detail focused for products-only refresh, without touching the main pool detail slice
function* getPoolforProducts(actions) {
	try {
		const requestOptions = createRequestOptions(actions.access);
		let url = buildDetailUrl(`/opa-pool/${actions.uuid}`);
		// Allow includes like in getPool (e.g., 'products&groupBy=cabanaCategory')
		if (actions.includes) {
			const includeValue = Array.isArray(actions.includes)
				? actions.includes.join(',')
				: String(actions.includes);
			const joinChar = url.includes('?') ? '&' : '?';
			url += `${joinChar}include=${includeValue}`;
		}
		const response = yield call(apiCall, 'get', url, requestOptions);
		yield put({ type: 'POOL_FOR_PRODUCTS_RESPONSE_GET', value: response });
	} catch (error) {
		yield put({ type: 'POOL_FOR_PRODUCTS_RESPONSE_GET', value: error });
	}
}

function* patchPool(actions) {
	try {
		const payload = sanitizePayload(actions.payload);
		const requestOptions = createRequestOptions(actions.access, payload);
		const url = buildDetailUrl(`/opa-pool/${actions.uuid}`);
		const response = yield call(apiCall, 'PATCH', url, requestOptions);
		yield put({
			type: 'POOL_RESPONSE_PATCH',
			value: buildMutationResponse(response),
		});
	} catch (error) {
		yield put({
			type: 'POOL_RESPONSE_PATCH',
			value: { ...error, responseStatus: 'error' },
		});
	}
}

function* deletePool(actions) {
	try {
		const requestOptions = createRequestOptions(actions.access);
		const url = buildDetailUrl(`/opa-pool/${actions.uuid}`);
		const response = yield call(apiCall, 'DELETE', url, requestOptions);
		yield put({
			type: 'POOL_RESPONSE_DELETE',
			value: buildMutationResponse(response),
		});
	} catch (error) {
		yield put({
			type: 'POOL_RESPONSE_DELETE',
			value: { ...error, responseStatus: 'error' },
		});
	}
}

function* deletePoolStatement(actions) {
	try {
		const requestOptions = createRequestOptions(actions.access);
		const url = buildDetailUrl(
			`/opa-pool-statements/${actions.poolUuid}/${actions.statementUuid}`
		);
		const response = yield call(apiCall, 'DELETE', url, requestOptions);
		yield put({
			type: 'POOL_STATEMENT_RESPONSE_DELETE',
			value: buildMutationResponse(response),
		});
	} catch (error) {
		yield put({
			type: 'POOL_STATEMENT_RESPONSE_DELETE',
			value: { ...error, responseStatus: 'error' },
		});
	}
}

function* deletePoolImage(actions) {
	try {
		const requestOptions = createRequestOptions(actions.access);
		const url = buildDetailUrl(
			`/opa-pool-image/${actions.poolUuid}/${actions.imageUuid}`
		);
		const response = yield call(apiCall, 'DELETE', url, requestOptions);
		yield put({
			type: 'POOL_IMAGE_RESPONSE_DELETE',
			value: buildMutationResponse(response),
		});
	} catch (error) {
		yield put({
			type: 'POOL_IMAGE_RESPONSE_DELETE',
			value: { ...error, responseStatus: 'error' },
		});
	}
}

function* deletePoolPoi(actions) {
	try {
		const requestOptions = createRequestOptions(actions.access);
		const url = buildDetailUrl(
			`/opa-pool-poi/${actions.poolUuid}/${actions.poiUuid}`
		);
		const response = yield call(apiCall, 'DELETE', url, requestOptions);
		yield put({
			type: 'POOL_POI_RESPONSE_DELETE',
			value: buildMutationResponse(response),
		});
	} catch (error) {
		yield put({
			type: 'POOL_POI_RESPONSE_DELETE',
			value: { ...error, responseStatus: 'error' },
		});
	}
}

function* resetPools() {
	yield put({ type: "POOLS_RESET" });
}

function* getPointsOfInterest(actions) {
	try {
		const requestOptions = createRequestOptions(actions.access);
		const url = buildListUrl(
			'/opa-points-of-interest',
			actions.query,
			actions.page
		);
		const response = yield call(apiCall, 'get', url, requestOptions);
		yield put({ type: 'POINTS_OF_INTEREST_RESPONSE_GET', value: response });
	} catch (error) {
		yield put({ type: 'POINTS_OF_INTEREST_RESPONSE_GET', value: error });
	}
}

function* resetPointsOfInterests() {
	yield put({ type: "POINTS_OF_INTEREST_RESPONSE_RESET" });
}

// Batch operation for saving Mapping with related data (images, products)
function* saveMappingWithRelatedData(action) {

	const {
		access,
		data = {},
		uuid,
		// Upsert arrays
		images = [],
		productsAdd = [],
		// Deletions
		removedProductUuids = [],
		removedImageUuids = [],
		requestId,
	} = action;

	const results = {
		success: true,
		operations: {},
		errors: [],
	};

	try {
		// 1) Create or update Mapping (main)
		let mappingUuid = uuid;
		let mainOperation;

		if (!uuid) {
			const createOptions = createRequestOptions(access, data);
			const url = appendEnvParam(`${apiUrl}/opa-mapping`);
			mainOperation = yield call(apiCall, 'POST', url, createOptions);

			const mutationResponse = buildMutationResponse(mainOperation);
			if (mutationResponse.responseStatus === 'success' && mainOperation.uuid) {
				mappingUuid = mainOperation.uuid;
				results.operations.main = { type: 'create', status: 'success', data: mainOperation };
			} else {
				yield put({
					type: 'MAPPING_BATCH_RESPONSE',
					value: { success: false, errors: ['Failed to create mapping'], operations: { main: { type: 'create', status: 'error', response: mainOperation } }, requestId },
				});
				return;
			}
		} else {
			// include x/y from action when present (edit mode should send new coordinates)
			const payload = sanitizePayload(data);
			if (typeof action.x !== 'undefined' && typeof action.y !== 'undefined') {
				payload.x = Number(action.x);
				payload.y = Number(action.y);
			}
			const updateOptions = createRequestOptions(access, payload);
			const url = buildDetailUrl(`/opa-mapping/${mappingUuid}`);
			mainOperation = yield call(apiCall, 'PATCH', url, updateOptions);

			const mutationResponse = buildMutationResponse(mainOperation);
			if (mutationResponse.responseStatus === 'success') {
				results.operations.main = { type: 'update', status: 'success', data: mainOperation };
			} else {
				yield put({
					type: 'MAPPING_BATCH_RESPONSE',
					value: { success: false, errors: ['Failed to update mapping'], operations: { main: { type: 'update', status: 'error', response: mainOperation } }, requestId },
				});
				return;
			}
		}

		// 2) Deletions first: products, images
		if (removedProductUuids && removedProductUuids.length > 0) {
			try {
				const requestOptions = createRequestOptions(access, removedProductUuids);
				const url = buildDetailUrl(`/opa-mapping-products/${mappingUuid}`);
				const response = yield call(apiCall, 'DELETE', url, requestOptions);
				results.operations.products = results.operations.products || {};
				results.operations.products.deletions = { status: (!response?.error ? 'success' : 'error'), response };
				if (response?.error) { results.success = false; results.errors.push('Failed to delete mapping products'); }
			} catch (error) {
				results.operations.products = results.operations.products || {};
				results.operations.products.deletions = { status: 'error', error };
				results.success = false; results.errors.push('Failed to delete mapping products');
			}
		}

		if (removedImageUuids && removedImageUuids.length > 0) {
			results.operations.images = results.operations.images || {};
			results.operations.images.deletions = [];
			for (const imageUuid of removedImageUuids) {
				try {
					const requestOptions = createRequestOptions(access);
					const url = buildDetailUrl(`/opa-mapping-image/${mappingUuid}/${imageUuid}`);
					const response = yield call(apiCall, 'DELETE', url, requestOptions);
					const ok = !response?.error;
					results.operations.images.deletions.push({ uuid: imageUuid, success: ok, response });
					if (!ok) { results.success = false; results.errors.push(`Failed to delete image ${imageUuid}`); }
				} catch (error) {
					results.operations.images.deletions.push({ uuid: imageUuid, success: false, error });
					results.success = false; results.errors.push(`Failed to delete image ${imageUuid}`);
				}
			}
		}

		// (removed) accessibility deletions

		// 3) Upserts: products add, images create/patch
		if (productsAdd && productsAdd.length > 0) {
			try {
				const payload = productsAdd.map((p) => (typeof p === 'string' ? p : p?.productUuid)).filter(Boolean);
				const requestOptions = createRequestOptions(access, payload);
				const url = buildDetailUrl(`/opa-mapping-products/${mappingUuid}`);
				const response = yield call(apiCall, 'POST', url, requestOptions);
				results.operations.products = results.operations.products || {};
				results.operations.products.upsert = { status: (!response?.error ? 'success' : 'error'), response };
				if (response?.error) { results.success = false; results.errors.push('Failed to add mapping products'); }
			} catch (error) {
				results.operations.products = results.operations.products || {};
				results.operations.products.upsert = { status: 'error', error };
				results.success = false; results.errors.push('Failed to add mapping products');
			}
		}

		const imagesToCreate = (images || []).filter((img) => img && !img._delete && !img.imageUuid && (img.uri));
		if (imagesToCreate.length > 0) {
			try {
				const payload = imagesToCreate.map((img, idx) => ({
					uri: img.uri,
					alt: img.alt || 'Mapping image',
					type: img.type || 'GALLERY',
					sortOrder: img.sortOrder ?? idx + 1,
					...(img.description ? { description: img.description } : {}),
				}));
				const requestOptions = createRequestOptions(access, payload);
				const url = buildDetailUrl(`/opa-mapping-images/${mappingUuid}`);
				const response = yield call(apiCall, 'POST', url, requestOptions);
				results.operations.images = results.operations.images || {};
				results.operations.images.create = { status: (!response?.error ? 'success' : 'error'), response };
				if (response?.error) { results.success = false; results.errors.push('Failed to create mapping images'); }
			} catch (error) {
				results.operations.images = results.operations.images || {};
				results.operations.images.create = { status: 'error', error };
				results.success = false; results.errors.push('Failed to create mapping images');
			}
		}

		const imagesToPatch = (images || []).filter((img) => img && !img._delete && img.imageUuid);
		if (imagesToPatch.length > 0) {
			results.operations.images = results.operations.images || {};
			results.operations.images.patch = [];
			for (const img of imagesToPatch) {
				try {
					const payload = sanitizePayload({ alt: img.alt, type: img.type, sortOrder: img.sortOrder, description: img.description });
					const requestOptions = createRequestOptions(access, payload);
					const url = buildDetailUrl(`/opa-mapping-image/${mappingUuid}/${img.imageUuid}`);
					const response = yield call(apiCall, 'PATCH', url, requestOptions);
					const status = (!response?.error ? 'success' : 'error');
					results.operations.images.patch.push({ imageUuid: img.imageUuid, status, response });
					if (status !== 'success') { results.success = false; results.errors.push(`Failed to update image ${img.imageUuid}`); }
				} catch (error) {
					results.operations.images.patch.push({ imageUuid: img.imageUuid, status: 'error', error });
					results.success = false; results.errors.push(`Failed to update image ${img.imageUuid}`);
				}
			}
		}

		// (removed) accessibility upserts

		// 4) Final response
		yield put({
			type: 'MAPPING_BATCH_RESPONSE',
			value: {
				success: results.success,
				operations: results.operations,
				errors: results.errors.length > 0 ? results.errors : null,
				requestId,
				// include a normalized responseStatus so UI effects can react uniformly
				responseStatus: results.success ? 'success' : 'error',
			},
		});
	} catch (error) {
		yield put({
			type: 'MAPPING_BATCH_RESPONSE',
			value: { success: false, errors: [error.message || 'Unknown error'], requestId },
		});
	}
}

// MAPPING: top-level CRUD
function* getMappings(actions) {
	try {
		const requestOptions = createRequestOptions(actions.access);
		// Supports filters via actions.query, paging via actions.page, and include via actions.includes
		const url = buildListUrl('/opa-mappings', actions.query, actions.page, actions.includes);
		const response = yield call(apiCall, 'get', url, requestOptions);
		yield put({ type: 'MAPPINGS_RESPONSE_GET', value: response });
	} catch (error) {
		yield put({ type: 'MAPPINGS_RESPONSE_GET', value: error });
	}
}

function* getMapping(actions) {
	try {
		const requestOptions = createRequestOptions(actions.access);
		let url = buildDetailUrl(`/opa-mapping/${actions.uuid}`);
		// allow includes like images,products,accessibility,pool
		if (actions.includes) {
			const includeValue = Array.isArray(actions.includes)
				? actions.includes.join(',')
				: String(actions.includes);
			const joinChar = url.includes('?') ? '&' : '?';
			url += `${joinChar}include=${includeValue}`;
		}
		const response = yield call(apiCall, 'get', url, requestOptions);
		yield put({ type: 'MAPPING_RESPONSE_GET', value: response });
	} catch (error) {
		yield put({ type: 'MAPPING_RESPONSE_GET', value: error });
	}
}

function* postMapping(actions) {
	try {
		const requestOptions = createRequestOptions(actions.access, actions.payload);
		const url = appendEnvParam(`${apiUrl}/opa-mapping`);
		const response = yield call(apiCall, 'POST', url, requestOptions);
		yield put({
			type: 'MAPPING_RESPONSE_POST',
			value: buildMutationResponse(response),
		});
	} catch (error) {
		yield put({
			type: 'MAPPING_RESPONSE_POST',
			value: { ...error, responseStatus: 'error' },
		});
	}
}

function* patchMapping(actions) {
	try {
		const payload = sanitizePayload(actions.payload);
		const requestOptions = createRequestOptions(actions.access, payload);
		const url = buildDetailUrl(`/opa-mapping/${actions.uuid}`);
		const response = yield call(apiCall, 'PATCH', url, requestOptions);
		yield put({
			type: 'MAPPING_RESPONSE_PATCH',
			value: buildMutationResponse(response),
		});
	} catch (error) {
		yield put({
			type: 'MAPPING_RESPONSE_PATCH',
			value: { ...error, responseStatus: 'error' },
		});
	}
}

function* deleteMapping(actions) {
	try {
		const requestOptions = createRequestOptions(actions.access);
		const url = buildDetailUrl(`/opa-mapping/${actions.uuid}`);
		const response = yield call(apiCall, 'DELETE', url, requestOptions);

		yield put({
			type: 'MAPPING_RESPONSE_DELETE',
			value: buildMutationResponse(response),
		});
	} catch (error) {
		yield put({
			type: 'MAPPING_RESPONSE_DELETE',
			value: { ...error, responseStatus: 'error' },
		});
	}
}

// MAPPING: PRODUCTS
function* getMappingProducts(actions) {
	try {
		const requestOptions = createRequestOptions(actions.access);
		let url = buildDetailUrl(`/opa-mapping/${actions.mappingUuid}`);
		const joinChar = url.includes('?') ? '&' : '?';
		url += `${joinChar}include=products`;
		const response = yield call(apiCall, 'get', url, requestOptions);
		yield put({ type: 'MAPPING_PRODUCTS_RESPONSE_GET', value: response });
	} catch (error) {
		yield put({ type: 'MAPPING_PRODUCTS_RESPONSE_GET', value: error });
	}
}

function* getMappingProduct(actions) {
	try {
		const requestOptions = createRequestOptions(actions.access);
		const normalize = (v) => String(v || '').split('/').pop();
		const mappingUuid = normalize(actions.mappingUuid);
		const productUuid = normalize(actions.productUuid);
		const url = buildDetailUrl(`/opa-mapping-product/${mappingUuid}/${productUuid}`);
		const response = yield call(apiCall, 'get', url, requestOptions);
		yield put({ type: 'MAPPING_PRODUCT_RESPONSE_GET', value: response });
	} catch (error) {
		yield put({ type: 'MAPPING_PRODUCT_RESPONSE_GET', value: error });
	}
}

function* postMappingProducts(actions) {
	try {
		const requestOptions = createRequestOptions(actions.access, actions.payload);
		const normalize = (v) => String(v || '').split('/').pop();
		const mappingUuid = normalize(actions.mappingUuid);
		const url = buildDetailUrl(`/opa-mapping-products/${mappingUuid}`);
		const response = yield call(apiCall, 'POST', url, requestOptions);
		yield put({
			type: 'MAPPING_PRODUCTS_RESPONSE_POST',
			value: buildMutationResponse(response),
		});
	} catch (error) {
		yield put({
			type: 'MAPPING_PRODUCTS_RESPONSE_POST',
			value: { ...error, responseStatus: 'error' },
		});
	}
}

function* deleteMappingProducts(actions) {
	try {
		const requestOptions = createRequestOptions(actions.access, actions.payload);
		const normalize = (v) => String(v || '').split('/').pop();
		const mappingUuid = normalize(actions.mappingUuid);
		const url = buildDetailUrl(`/opa-mapping-products/${mappingUuid}`);
		const response = yield call(apiCall, 'DELETE', url, requestOptions);
		yield put({
			type: 'MAPPING_PRODUCTS_RESPONSE_DELETE',
			value: buildMutationResponse(response),
		});
	} catch (error) {
		yield put({
			type: 'MAPPING_PRODUCTS_RESPONSE_DELETE',
			value: { ...error, responseStatus: 'error' },
		});
	}
}

// MAPPING: IMAGES
function* getMappingImages(actions) {
	try {
		const requestOptions = createRequestOptions(actions.access);
		let url = buildDetailUrl(`/opa-mapping/${actions.mappingUuid}`);
		const joinChar = url.includes('?') ? '&' : '?';
		url += `${joinChar}include=images`;
		const response = yield call(apiCall, 'get', url, requestOptions);
		yield put({ type: 'MAPPING_IMAGES_RESPONSE_GET', value: response });
	} catch (error) {
		yield put({ type: 'MAPPING_IMAGES_RESPONSE_GET', value: error });
	}
}

function* getMappingImage(actions) {
	try {
		const requestOptions = createRequestOptions(actions.access);
		const normalize = (v) => String(v || '').split('/').pop();
		const mappingUuid = normalize(actions.mappingUuid);
		const imageUuid = normalize(actions.imageUuid);
		const url = buildDetailUrl(`/opa-mapping-image/${mappingUuid}/${imageUuid}`);
		const response = yield call(apiCall, 'get', url, requestOptions);
		yield put({ type: 'MAPPING_IMAGE_RESPONSE_GET', value: response });
	} catch (error) {
		yield put({ type: 'MAPPING_IMAGE_RESPONSE_GET', value: error });
	}
}

function* postMappingImages(actions) {
	try {
		const requestOptions = createRequestOptions(actions.access, actions.payload);
		const normalize = (v) => String(v || '').split('/').pop();
		const mappingUuid = normalize(actions.mappingUuid);
		const url = buildDetailUrl(`/opa-mapping-images/${mappingUuid}`);
		const response = yield call(apiCall, 'POST', url, requestOptions);
		yield put({
			type: 'MAPPING_IMAGES_RESPONSE_POST',
			value: buildMutationResponse(response),
		});
	} catch (error) {
		yield put({
			type: 'MAPPING_IMAGES_RESPONSE_POST',
			value: { ...error, responseStatus: 'error' },
		});
	}
}

function* patchMappingImage(actions) {
	try {
		const payload = sanitizePayload(actions.payload);
		const requestOptions = createRequestOptions(actions.access, payload);
		const normalize = (v) => String(v || '').split('/').pop();
		const mappingUuid = normalize(actions.mappingUuid);
		const imageUuid = normalize(actions.imageUuid);
		const url = buildDetailUrl(`/opa-mapping-image/${mappingUuid}/${imageUuid}`);
		const response = yield call(apiCall, 'PATCH', url, requestOptions);
		yield put({
			type: 'MAPPING_IMAGE_RESPONSE_PATCH',
			value: buildMutationResponse(response),
		});
	} catch (error) {
		yield put({
			type: 'MAPPING_IMAGE_RESPONSE_PATCH',
			value: { ...error, responseStatus: 'error' },
		});
	}
}

function* deleteMappingImage(actions) {
	try {
		const requestOptions = createRequestOptions(actions.access);
		const normalize = (v) => String(v || '').split('/').pop();
		const mappingUuid = normalize(actions.mappingUuid);
		const imageUuid = normalize(actions.imageUuid);
		const url = buildDetailUrl(`/opa-mapping-image/${mappingUuid}/${imageUuid}`);
		const response = yield call(apiCall, 'DELETE', url, requestOptions);
		yield put({
			type: 'MAPPING_IMAGE_RESPONSE_DELETE',
			value: buildMutationResponse(response),
		});
	} catch (error) {
		yield put({
			type: 'MAPPING_IMAGE_RESPONSE_DELETE',
			value: { ...error, responseStatus: 'error' },
		});
	}
}

// (Removed) MAPPING: ACCESSIBILITY — relation no longer exists

// Batch operation for saving Property Map with related data (images, areas, POIs)
function* savePropertyMapWithRelatedData(action) {
	const {
		access,
		data,
		uuid,
		images = [],
		areas = [],
		pointsOfInterest = [],
		removedAreaUuids = [],
		removedPoiUuids = [],
		requestId,
	} = action;

	const results = {
		success: true,
		operations: {},
		errors: [],
	};

	try {
		// 1) Create or update Property Map (main)
		let propertyMapUuid = uuid;
		let mainOperation;

		if (!uuid) {
			const createOptions = createRequestOptions(access, data);
			const url = appendEnvParam(`${apiUrl}/opa-property-map`);
			mainOperation = yield call(apiCall, 'POST', url, createOptions);

			const mutationResponse = buildMutationResponse(mainOperation);
			if (mutationResponse.responseStatus === 'success' && mainOperation.uuid) {
				propertyMapUuid = mainOperation.uuid;
				results.operations.main = { type: 'create', status: 'success', data: mainOperation };
			} else {
				yield put({
					type: 'PROPERTY_MAP_BATCH_RESPONSE',
					value: { success: false, errors: ['Failed to create property map'], operations: { main: { type: 'create', status: 'error', response: mainOperation } } },
				});
				return;
			}
		} else {
			const payload = sanitizePayload(data);
			const hasPayload = payload && Object.keys(payload).length > 0;
			if (hasPayload) {
				const updateOptions = createRequestOptions(access, payload);
				const url = buildDetailUrl(`/opa-property-map/${propertyMapUuid}`);
				mainOperation = yield call(apiCall, 'PATCH', url, updateOptions);

				const mutationResponse = buildMutationResponse(mainOperation);
				if (mutationResponse.responseStatus === 'success') {
					results.operations.main = { type: 'update', status: 'success', data: mainOperation };
				} else {
					yield put({
						type: 'PROPERTY_MAP_BATCH_RESPONSE',
						value: { success: false, errors: ['Failed to update property map'], operations: { main: { type: 'update', status: 'error', response: mainOperation } } },
					});
					return;
				}
			} else {
				// Skip main PATCH when there's no data to update, rely on upsert step below
				results.operations.main = { type: 'update', status: 'skipped', reason: 'empty-payload' };
			}
		}

		// 2) DELETE operations for Areas/POIs (sequential capturing)
		if (removedAreaUuids.length > 0) {
			results.operations.areas = results.operations.areas || {};
			results.operations.areas.deletions = [];
			for (const areaUuid of removedAreaUuids) {
				try {
					const requestOptions = createRequestOptions(access);
					const url = buildDetailUrl(`/opa-property-map-area/${propertyMapUuid}/${areaUuid}`);
					const response = yield call(apiCall, 'DELETE', url, requestOptions);
					const ok = !response?.error;
					results.operations.areas.deletions.push({ uuid: areaUuid, success: ok, response });
					if (!ok) { results.success = false; results.errors.push(`Failed to delete area ${areaUuid}`); }
				} catch (error) {
					results.operations.areas.deletions.push({ uuid: areaUuid, success: false, error });
					results.success = false; results.errors.push(`Failed to delete area ${areaUuid}`);
				}
			}
		}

		if (removedPoiUuids.length > 0) {
			results.operations.pois = results.operations.pois || {};
			results.operations.pois.deletions = [];
			for (const poiUuid of removedPoiUuids) {
				try {
					const requestOptions = createRequestOptions(access);
					const url = buildDetailUrl(`/opa-property-map-poi/${propertyMapUuid}/${poiUuid}`);
					const response = yield call(apiCall, 'DELETE', url, requestOptions);
					const ok = !response?.error;
					results.operations.pois.deletions.push({ uuid: poiUuid, success: ok, response });
					if (!ok) { results.success = false; results.errors.push(`Failed to delete POI ${poiUuid}`); }
				} catch (error) {
					results.operations.pois.deletions.push({ uuid: poiUuid, success: false, error });
					results.success = false; results.errors.push(`Failed to delete POI ${poiUuid}`);
				}
			}
		}

		// 3) Upsert Areas/POIs/Images via PATCH (arrays: new + updated)
		// Images rule: creations/updates go via PATCH images[], deletions stay as DELETE calls
		const imagesForPatch = (images || [])
			.filter((img) => img && img._delete !== true)
			.map((img) => {
				if (img.imageUuid) {
					const payload = { uuid: img.imageUuid };
					if (img.alt !== undefined) payload.alt = img.alt;
					if (img.type) payload.type = img.type;
					return payload;
				}
				return { uri: img.uri, alt: img.alt || 'Property map image', type: img.type };
			});

		if ((areas && areas.length) || (pointsOfInterest && pointsOfInterest.length) || (imagesForPatch && imagesForPatch.length)) {
			try {
				const upsertPayload = {};
				if (areas && areas.length) upsertPayload.areas = areas;
				if (pointsOfInterest && pointsOfInterest.length) upsertPayload.pointsOfInterest = pointsOfInterest;
				if (imagesForPatch && imagesForPatch.length) upsertPayload.images = imagesForPatch;

				const requestOptions = createRequestOptions(access, upsertPayload);
				const url = buildDetailUrl(`/opa-property-map/${propertyMapUuid}`);
				const response = yield call(apiCall, 'PATCH', url, requestOptions);

				const mutationResponse = buildMutationResponse(response);
				results.operations.areas = results.operations.areas || {};
				results.operations.pois = results.operations.pois || {};
				results.operations.images = results.operations.images || {};
				results.operations.areas.upsert = { status: mutationResponse.responseStatus, response };
				results.operations.pois.upsert = { status: mutationResponse.responseStatus, response };
				results.operations.images.upsert = { status: mutationResponse.responseStatus, response };

				if (mutationResponse.responseStatus !== 'success') {
					results.success = false;
					results.errors.push('Failed to upsert property map data (areas/pois/images)');
				}
			} catch (error) {
				results.operations.areas = results.operations.areas || {};
				results.operations.pois = results.operations.pois || {};
				results.operations.images = results.operations.images || {};
				results.operations.areas.upsert = { status: 'error', error };
				results.operations.pois.upsert = { status: 'error', error };
				results.operations.images.upsert = { status: 'error', error };
				results.success = false;
				results.errors.push('Failed to upsert property map data (areas/pois/images)');
			}
		}

		// 4) Images deletions only (creations/updates done in PATCH above)
		const imageEffects = [];
		const imagesToDelete = images.filter((img) => img.imageUuid && img._delete === true);

		imagesToDelete.forEach((image, index) => {
			imageEffects.push(call(function* () {
				try {
					const deleteOptions = createRequestOptions(access);
					const deleteUrl = buildDetailUrl(`/opa-property-map-image/${propertyMapUuid}/${image.imageUuid}`);
					const response = yield call(apiCall, 'DELETE', deleteUrl, deleteOptions);
					return { operation: 'delete_image', imageUuid: image.imageUuid, index, success: response && !response.error, response };
				} catch (error) {
					return { operation: 'delete_image', imageUuid: image.imageUuid, index, success: false, error };
				}
			}));
		});

		if (imageEffects.length > 0) {
			const imageResults = yield all(imageEffects);
			results.operations.images = results.operations.images || {};
			results.operations.images.deletions = results.operations.images.deletions || [];
			imageResults.forEach((r) => {
				if (r.operation === 'delete_image') {
					results.operations.images.deletions.push(r);
					if (!r.success) { results.success = false; results.errors.push(`Failed to delete image ${r.imageUuid}`); }
				}
			});
		}

		// 5) Final response
		yield put({
			type: 'PROPERTY_MAP_BATCH_RESPONSE',
			value: {
				success: results.success,
				uuid: propertyMapUuid,
				operations: results.operations,
				errors: results.errors.length > 0 ? results.errors : null,
				requestId,
			},
		});
	} catch (error) {
		yield put({
			type: 'PROPERTY_MAP_BATCH_RESPONSE',
			value: { success: false, errors: [error.message || 'Unknown error'], requestId },
		});
	}
}

function* resetPoolAdd() {
	yield put({ type: "POOL_ADD_RESET" });
}


function* deleteProductPools(actions) {
	try {
		const requestOptions = createRequestOptions(actions.access, actions.payload);
		const normalize = (v) => String(v || '').split('/').pop();
		const productUuid = normalize(actions.productUuid || actions.uuid);
		const url = buildDetailUrl(`/opa-product-pools/${productUuid}`);
		const response = yield call(apiCall, 'DELETE', url, requestOptions);
		yield put({
			type: 'PRODUCT_POOLS_RESPONSE_DELETE',
			value: buildMutationResponse(response),
		});
	} catch (error) {
		yield put({
			type: 'PRODUCT_POOLS_RESPONSE_DELETE',
			value: { ...error, responseStatus: 'error' },
		});
	}
}

export function* watchCabanasMapping() {
	yield all([
		// STATEMENTS: CRUD for statements
		takeEvery('POST_STATEMENT', postStatement),
		takeEvery('GET_STATEMENTS', getStatements),
		takeEvery('GET_STATEMENT', getStatement),
		takeEvery('PATCH_STATEMENT', patchStatement),
		takeEvery('DELETE_STATEMENT', deleteStatement),

		// PROPERTY MAP: top-level CRUD & related deletions
		takeEvery('POST_PROPERTY_MAP', postPropertyMap),
		takeEvery('GET_PROPERTY_MAPS', getPropertyMaps),
		takeEvery('GET_PROPERTY_MAP', getPropertyMap),
		takeEvery('PATCH_PROPERTY_MAP', patchPropertyMap),
		takeEvery('DELETE_PROPERTY_MAP', deletePropertyMap),
		takeEvery('DELETE_PROPERTY_MAP_IMAGE', deletePropertyMapImage),
		takeEvery('DELETE_PROPERTY_MAP_AREA', deletePropertyMapArea),
		takeEvery('DELETE_PROPERTY_MAP_POI', deletePropertyMapPoi),

		// AREAS: CRUD and related operations
		takeEvery('POST_AREA', postArea),
		takeEvery('GET_AREAS', getAreas),
		takeEvery('GET_AREA', getArea),
		takeEvery('PATCH_AREA', patchArea),
		takeEvery('DELETE_AREA', deleteArea),
		takeEvery('DELETE_AREA_POOL', deleteAreaPool),
		takeEvery('DELETE_AREA_POI', deleteAreaPoi),
		takeEvery('DELETE_AREA_IMAGE', deleteAreaImage),
		takeEvery('RESET_AREAS', resetAreas),

		// POOLS: CRUD and related operations
		takeEvery('POST_POOL', postPool),
		takeEvery('GET_POOLS', getPools),
		takeEvery('GET_POOL', getPool),
		// Products-only refresh for pool
		takeEvery('GET_POOL_FOR_PRODUCTS', getPoolforProducts),
		takeEvery('PATCH_POOL', patchPool),
		takeEvery('DELETE_POOL', deletePool),
		takeEvery('DELETE_POOL_STATEMENT', deletePoolStatement),
		takeEvery('DELETE_POOL_POI', deletePoolPoi),
		takeEvery('DELETE_POOL_IMAGE', deletePoolImage),
		// local state reset for pool add flow
		takeEvery('RESET_ADD_POOL_STATE', resetPoolAdd),
		takeEvery('RESET_POOLS', resetPools),

		// POINTS OF INTEREST: listing
		takeEvery('GET_POINTS_OF_INTEREST', getPointsOfInterest),
		takeEvery('RESET_POINTS_OF_INTEREST', resetPointsOfInterests),

		// PROPERTY MAP: batch orchestration (upsert/delete areas, pois, images)
		takeEvery('SAVE_PROPERTY_MAP_BATCH', savePropertyMapWithRelatedData),

		// AREA: batch orchestration (upsert/delete pools, pois, images)
		takeEvery('SAVE_AREA_BATCH', saveAreaWithRelatedData),

		// POOL: batch orchestration (upsert/delete images, pois, statements, products)
		takeEvery('SAVE_POOL_BATCH', savePoolWithRelatedData),

		// MAPPING: batch orchestration
		takeEvery('SAVE_MAPPING_BATCH', saveMappingWithRelatedData),

		// MAPPING: top-level CRUD
		takeEvery('GET_MAPPINGS', getMappings),
		takeEvery('GET_MAPPING', getMapping),
		takeEvery('POST_MAPPING', postMapping),
		takeEvery('PATCH_MAPPING', patchMapping),
		takeEvery('DELETE_MAPPING', deleteMapping),

		// MAPPING: PRODUCTS
		takeEvery('GET_MAPPING_PRODUCTS', getMappingProducts),
		takeEvery('GET_MAPPING_PRODUCT', getMappingProduct),
		takeEvery('POST_MAPPING_PRODUCTS', postMappingProducts),
		takeEvery('DELETE_MAPPING_PRODUCTS', deleteMappingProducts),

		// MAPPING: IMAGES
		takeEvery('GET_MAPPING_IMAGES', getMappingImages),
		takeEvery('GET_MAPPING_IMAGE', getMappingImage),
		takeEvery('POST_MAPPING_IMAGES', postMappingImages),
		takeEvery('PATCH_MAPPING_IMAGE', patchMappingImage),
		takeEvery('DELETE_MAPPING_IMAGE', deleteMappingImage),

		// PRODUCT / POOLS relation delete (DELETE /opa-product-pools/{product_uuid})
		// body can be an array of pool uuids, e.g. ["uuid1","uuid2",...]
		takeEvery('DELETE_PRODUCT_POOLS', deleteProductPools),

		// OTHER: cascade delete for property map (helper flow)
		takeEvery('DELETE_PROPERTY_MAP_CASCADE', deletePropertyMapCascade),
	]);
}

// Batch operation for saving Area with related data (images, pools, POIs)
function* saveAreaWithRelatedData(action) {

	const {
		access,
		data,
		uuid,
		images = [],
		pools = [],
		pointsOfInterest = [],
		removedPoolUuids = [],
		removedPoiUuids = [],
		requestId,
	} = action;

	const results = {
		success: true,
		operations: {},
		errors: [],
	};

	try {
		// 1) Create or update Area (main)
		let areaUuid = uuid;
		let mainOperation;

		if (!uuid) {
			const createOptions = createRequestOptions(access, data);
			const url = appendEnvParam(`${apiUrl}/opa-area`);
			mainOperation = yield call(apiCall, 'POST', url, createOptions);

			const mutationResponse = buildMutationResponse(mainOperation);
			if (mutationResponse.responseStatus === 'success' && mainOperation.uuid) {
				areaUuid = mainOperation.uuid;
				results.operations.main = { type: 'create', status: 'success', data: mainOperation };
			} else {
				yield put({
					type: 'AREA_BATCH_RESPONSE',
					value: { success: false, errors: ['Failed to create area'], operations: { main: { type: 'create', status: 'error', response: mainOperation } } },
				});
				return;
			}
		} else {
			const payload = sanitizePayload(data);
			const updateOptions = createRequestOptions(access, payload);
			const url = buildDetailUrl(`/opa-area/${areaUuid}`);
			mainOperation = yield call(apiCall, 'PATCH', url, updateOptions);

			const mutationResponse = buildMutationResponse(mainOperation);
			if (mutationResponse.responseStatus === 'success') {
				results.operations.main = { type: 'update', status: 'success', data: mainOperation };
			} else {
				yield put({
					type: 'AREA_BATCH_RESPONSE',
					value: { success: false, errors: ['Failed to update area'], operations: { main: { type: 'update', status: 'error', response: mainOperation } } },
				});
				return;
			}
		}

		// Helpers to normalize ids (avoid duplicated segments like 'areaUuid/poolUuid')
		const normalizeId = (v) => String(v || '').split('/').pop();
		const unique = (arr) => Array.from(new Set(arr.filter(Boolean)));

		// 2) DELETE operations for Pools/POIs (sequential)
		const poolsToDelete = unique((removedPoolUuids || []).map(normalizeId));
		if (poolsToDelete.length > 0) {
			results.operations.pools = results.operations.pools || {};
			results.operations.pools.deletions = [];
			for (const poolUuid of poolsToDelete) {
				try {
					const requestOptions = createRequestOptions(access);
					const url = buildDetailUrl(`/opa-area-pool/${areaUuid}/${poolUuid}`);
					const response = yield call(apiCall, 'DELETE', url, requestOptions);
					const ok = !response?.error;
					results.operations.pools.deletions.push({ uuid: poolUuid, success: ok, response });
					if (!ok) { results.success = false; results.errors.push(`Failed to delete pool ${poolUuid}`); }
				} catch (error) {
					results.operations.pools.deletions.push({ uuid: poolUuid, success: false, error });
					results.success = false; results.errors.push(`Failed to delete pool ${poolUuid}`);
				}
			}
		}

		const poisToDelete = unique((removedPoiUuids || []).map(normalizeId));
		if (poisToDelete.length > 0) {
			results.operations.pois = results.operations.pois || {};
			results.operations.pois.deletions = [];
			for (const poiUuid of poisToDelete) {
				try {
					const requestOptions = createRequestOptions(access);
					const url = buildDetailUrl(`/opa-area-poi/${areaUuid}/${poiUuid}`);
					const response = yield call(apiCall, 'DELETE', url, requestOptions);
					const ok = !response?.error;
					results.operations.pois.deletions.push({ uuid: poiUuid, success: ok, response });
					if (!ok) { results.success = false; results.errors.push(`Failed to delete POI ${poiUuid}`); }
				} catch (error) {
					results.operations.pois.deletions.push({ uuid: poiUuid, success: false, error });
					results.success = false; results.errors.push(`Failed to delete POI ${poiUuid}`);
				}
			}
		}

		// 3) Upsert Pools/POIs/Images via PATCH (arrays: new + updated)
		// Images rule: creations/updates go via PATCH images[], deletions stay as DELETE calls
		const imagesForPatch = (images || [])
			.filter((img) => img && img._delete !== true)
			.map((img) => {
				if (img.imageUuid) {
					const payload = { uuid: img.imageUuid };
					if (img.alt !== undefined) payload.alt = img.alt;
					if (img.type) payload.type = img.type;
					return payload;
				}
				return { uri: img.uri, alt: img.alt || 'Area map image', type: img.type };
			});

		if ((pools && pools.length) || (pointsOfInterest && pointsOfInterest.length) || (imagesForPatch && imagesForPatch.length)) {
			try {
				const upsertPayload = {};
				if (pools && pools.length) upsertPayload.pools = pools;
				if (pointsOfInterest && pointsOfInterest.length) upsertPayload.pointsOfInterest = pointsOfInterest;
				if (imagesForPatch && imagesForPatch.length) upsertPayload.images = imagesForPatch;

				const requestOptions = createRequestOptions(access, upsertPayload);
				const url = buildDetailUrl(`/opa-area/${areaUuid}`);
				const response = yield call(apiCall, 'PATCH', url, requestOptions);

				const mutationResponse = buildMutationResponse(response);
				results.operations.pools = results.operations.pools || {};
				results.operations.pois = results.operations.pois || {};
				results.operations.images = results.operations.images || {};
				results.operations.pools.upsert = { status: mutationResponse.responseStatus, response };
				results.operations.pois.upsert = { status: mutationResponse.responseStatus, response };
				results.operations.images.upsert = { status: mutationResponse.responseStatus, response };

				if (mutationResponse.responseStatus !== 'success') {
					results.success = false;
					results.errors.push('Failed to upsert area data (pools/pois/images)');
				}
			} catch (error) {
				results.operations.pools = results.operations.pools || {};
				results.operations.pois = results.operations.pois || {};
				results.operations.images = results.operations.images || {};
				results.operations.pools.upsert = { status: 'error', error };
				results.operations.pois.upsert = { status: 'error', error };
				results.operations.images.upsert = { status: 'error', error };
				results.success = false;
				results.errors.push('Failed to upsert area data (pools/pois/images)');
			}
		}

		// 4) Images deletions only (creations/updates done in PATCH above)
		const imageEffects = [];
		const imagesToDelete = images.filter((img) => img.imageUuid && img._delete === true);

		// imagesToAdd.forEach((image, index) => {
		// 	imageEffects.push(call(function* () {
		// 		try {
		// 			const imagePayload = { uri: image.uri, alt: image.alt || 'Area map image', type: image.type };
		// 			const imageOptions = createRequestOptions(access, imagePayload);
		// 			const imageUrl = buildDetailUrl(`/opa-area-image/${areaUuid}`);
		// 			const response = yield call(apiCall, 'POST', imageUrl, imageOptions);
		// 			return { operation: 'upload_image', index, success: response && !response.error, response };
		// 		} catch (error) {
		// 			return { operation: 'upload_image', index, success: false, error };
		// 		}
		// 	}));
		// });

		imagesToDelete.forEach((image, index) => {
			imageEffects.push(call(function* () {
				try {
					const deleteOptions = createRequestOptions(access);
					const imageUuid = normalizeId(image.imageUuid);
					const deleteUrl = buildDetailUrl(`/opa-area-image/${areaUuid}/${imageUuid}`);
					const response = yield call(apiCall, 'DELETE', deleteUrl, deleteOptions);
					return { operation: 'delete_image', imageUuid, index, success: response && !response.error, response };
				} catch (error) {
					const imageUuid = normalizeId(image.imageUuid);
					return { operation: 'delete_image', imageUuid, index, success: false, error };
				}
			}));
		});

		if (imageEffects.length > 0) {
			const imageResults = yield all(imageEffects);
			results.operations.images = results.operations.images || {};
			results.operations.images.deletions = results.operations.images.deletions || [];
			imageResults.forEach((r) => {
				if (r.operation === 'delete_image') {
					results.operations.images.deletions.push(r);
					if (!r.success) { results.success = false; results.errors.push(`Failed to delete image ${r.imageUuid}`); }
				}
			});
		}

		// 5) Final response
		yield put({
			type: 'AREA_BATCH_RESPONSE',
			value: {
				success: results.success,
				uuid: areaUuid,
				operations: results.operations,
				errors: results.errors.length > 0 ? results.errors : null,
				requestId,
			},
		});
	} catch (error) {
		yield put({
			type: 'AREA_BATCH_RESPONSE',
			value: { success: false, errors: [error.message || 'Unknown error'], requestId },
		});
	}

}

// Batch operation for saving Pool with related data (images, POIs, products)
function* savePoolWithRelatedData(action) {
	try {
		const {
			access,
			data = {},
			uuid,
			images = [],
			pointsOfInterest = [],
			removedPoiUuids = [],
			// new: statements support
			statements = [],
			removedStatementUuids = [],
			products = [],
		} = action;

		const results = {
			success: true,
			operations: {},
			errors: [],
		};

		// CREATE flow
		if (!uuid) {
			const payload = {
				...data,
				// Only include arrays when they have content
				...(Array.isArray(images) && images.length
					? { images: images.filter((i) => i.uri && !i._delete) }
					: {}),
				...(Array.isArray(pointsOfInterest) && pointsOfInterest.length
					? { pointsOfInterest }
					: {}),
				...(Array.isArray(statements) && statements.length ? { statements } : {}),
				...(Array.isArray(products) && products.length ? { products } : {}),
			};
			const requestOptions = createRequestOptions(access, payload);
			const url = appendEnvParam(`${apiUrl}/opa-pool`);
			const response = yield call(apiCall, 'POST', url, requestOptions);
			results.operations.createPool = buildMutationResponse(response);
			yield put({ type: 'POOL_RESPONSE_BATCH', value: results.operations.createPool });
			return;
		}

		// UPDATE flow
		const poolUuid = String(uuid).split('/').pop();

		// 1) Deletes first (images, pois, statements)
		for (const img of images) {
			if (img && img._delete && img.imageUuid) {
				try {
					const requestOptions = createRequestOptions(access);
					const url = buildDetailUrl(`/opa-pool-image/${poolUuid}/${img.imageUuid}`);
					const delResp = yield call(apiCall, 'DELETE', url, requestOptions);
					results.operations[`deleteImage:${img.imageUuid}`] = buildMutationResponse(delResp);
				} catch (err) {
					results.errors.push(err);
					results.success = false;
				}
			}
		}

		for (const poiUuid of removedPoiUuids || []) {
			if (!poiUuid) continue;
			try {
				const requestOptions = createRequestOptions(access);
				const url = buildDetailUrl(`/opa-pool-poi/${poolUuid}/${poiUuid}`);
				const delResp = yield call(apiCall, 'DELETE', url, requestOptions);
				results.operations[`deletePoi:${poiUuid}`] = buildMutationResponse(delResp);
			} catch (err) {
				results.errors.push(err);
				results.success = false;
			}
		}

		for (const stUuid of removedStatementUuids || []) {
			if (!stUuid) continue;
			try {
				const requestOptions = createRequestOptions(access);
				const url = buildDetailUrl(`/opa-pool-statements/${poolUuid}/${stUuid}`);
				const delResp = yield call(apiCall, 'DELETE', url, requestOptions);
				results.operations[`deleteStatement:${stUuid}`] = buildMutationResponse(delResp);
			} catch (err) {
				results.errors.push(err);
				results.success = false;
			}
		}

		// 2) Patch pool with additions/updates
		const imagesToAdd = (images || []).filter((i) => i && !i._delete && i.uri);
		const patchPayload = {
			...data,
			...(imagesToAdd.length ? { images: imagesToAdd } : {}),
			...(Array.isArray(pointsOfInterest) && pointsOfInterest.length
				? { pointsOfInterest }
				: {}),
			...(Array.isArray(statements) && statements.length ? { statements } : {}),
			...(Array.isArray(products) && products.length ? { products } : {}),
		};
		const requestOptions = createRequestOptions(access, sanitizePayload(patchPayload));
		const patchUrl = buildDetailUrl(`/opa-pool/${poolUuid}`);
		const patchResp = yield call(apiCall, 'PATCH', patchUrl, requestOptions);
		results.operations.patchPool = buildMutationResponse(patchResp);

		// 3) Final batch outcome
		const finalStatus = results.operations.patchPool?.responseStatus || (results.success ? 'success' : 'error');
		yield put({ type: 'POOL_RESPONSE_BATCH', value: { ...results, responseStatus: finalStatus } });
	} catch (error) {
		yield put({ type: 'POOL_RESPONSE_BATCH', value: { ...error, responseStatus: 'error' } });
	}
}

function* deletePropertyMapCascade(action) {
	try {
		// Optional trace for debugging
		const requestOptions = createRequestOptions(action.access);
		const normalize = (v) => String(v || '').split('/').pop();
		const propertyMapUuid = normalize(action.uuid);

		// 1) Fetch relations if not provided
		let areaUuids = (action.areaUuids || []).map(normalize);
		let poiUuids = (action.poiUuids || []).map(normalize);

		if (areaUuids.length === 0 && poiUuids.length === 0) {
			let url = buildDetailUrl(`/opa-property-map/${propertyMapUuid}`);
			const joinChar = url.includes('?') ? '&' : '?';
			url = `${url}${joinChar}include=areas,points_of_interest`;

			const detail = yield call(apiCall, 'get', url, requestOptions);
			const areas = Array.isArray(detail?.areas) ? detail.areas : [];
			const pois = Array.isArray(detail?.pointsOfInterest) ? detail.pointsOfInterest : [];

			areaUuids = areas.map((a) => normalize(a.uuid)).filter(Boolean);
			poiUuids = pois.map((p) => normalize(p.uuid)).filter(Boolean);
		}

		// 2) Delete related Areas
		for (const aUuid of Array.from(new Set(areaUuids))) {
			const url = buildDetailUrl(`/opa-property-map-area/${propertyMapUuid}/${aUuid}`);
			const resp = yield call(apiCall, 'DELETE', url, requestOptions);
			if (resp?.status && resp.status >= 400) {
				yield put({
					type: 'PROPERTY_MAP_RESPONSE_DELETE',
					value: { responseStatus: 'error', message: `Failed to delete area ${aUuid}` },
				});
				return;
			}
		}

		// 3) Delete related POIs
		for (const pUuid of Array.from(new Set(poiUuids))) {
			const url = buildDetailUrl(`/opa-property-map-poi/${propertyMapUuid}/${pUuid}`);
			const resp = yield call(apiCall, 'DELETE', url, requestOptions);
			if (resp?.status && resp.status >= 400) {
				yield put({
					type: 'PROPERTY_MAP_RESPONSE_DELETE',
					value: { responseStatus: 'error', message: `Failed to delete POI ${pUuid}` },
				});
				return;
			}
		}

		// 4) Finally delete the Property Map
		const delUrl = buildDetailUrl(`/opa-property-map/${propertyMapUuid}`);
		const finalResp = yield call(apiCall, 'DELETE', delUrl, requestOptions);
		yield put({
			type: 'PROPERTY_MAP_RESPONSE_DELETE',
			value: buildMutationResponse(finalResp),
		});
	} catch (error) {
		yield put({
			type: 'PROPERTY_MAP_RESPONSE_DELETE',
			value: { ...error, responseStatus: 'error' },
		});
	}
}
